import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, Search, Filter, Check, ChevronsUpDown } from "lucide-react";
import { assignmentService, documentService } from "@/services/documentService";
import { axiosInstance } from "@/pages/auth/auth.context";
import { DocumentAssignment, AssignmentFilters } from "@/types/document";
import { AssignmentTable } from "../../../components/assignments/AssignmentTable";
import { CreateAssignmentModal } from "../../../components/assignments/CreateAssignmentModal";
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const AssignmentManagement = () => {
  const [filters, setFilters] = useState<AssignmentFilters>({
    page: 0,
    size: 20,
    statuses: [],
    userIds: [],
    documentIds: [],
  });

  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filter states for UI
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null>(null);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState<string>("");
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const queryClient = useQueryClient();

  // Available filter options
  const statusOptions = [
    { value: "PENDING", label: "Pending" },
    { value: "SIGNED", label: "Signed" },
  ];

  const filterButtonClass =
    "transition-colors px-3 py-1 text-xs font-medium rounded-full border";

  // Fetch users for filter options
  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await axiosInstance.get("user?limit=100");
      return response.data;
    },
  });

  // Fetch documents for filter options
  const { data: documents } = useQuery({
    queryKey: ["documents"],
    queryFn: () => documentService.getDocuments({ size: 100 }),
  });

  // Update filters when individual filter states change
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      statuses: selectedStatuses,
      userIds: selectedUser ? [selectedUser.id] : [],
      documentIds: selectedDocumentIds,
      page: 0, // Reset to first page when filters change
    }));
  }, [selectedStatuses, selectedUser, selectedDocumentIds]);

  // Fetch assignments with current filters
  const {
    data: assignmentsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["assignments", filters],
    queryFn: () => assignmentService.getAssignments(filters),
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: assignmentService.deleteAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assignments"] });
      toast({
        title: "Success",
        description: "Assignment deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete assignment",
        variant: "destructive",
      });
    },
  });

  const handleDelete = async (assignment: DocumentAssignment) => {
    if (window.confirm(`Are you sure you want to delete this assignment?`)) {
      deleteAssignmentMutation.mutate(assignment.id);
    }
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const getStatusStats = () => {
    if (!assignmentsData) return null;

    const assignments = assignmentsData.content;

    const pending = assignments.filter((a) => a.status === "PENDING").length;
    const signed = assignments.filter((a) => a.status === "SIGNED").length;

    return { pending, signed };
  };

  const stats = getStatusStats();

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive">
              Error loading assignments. Please try again.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Assignment Management
          </h1>
          <p className="text-muted-foreground">
            Manage document assignments and track signing progress
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Assignment
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-warning">
                {stats.pending}
              </div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-success">
                {stats.signed}
              </div>
              <div className="text-sm text-muted-foreground">Signed</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Enhanced Filters */}
      <div className="mb-6">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="font-semibold px-6 py-2 rounded-lg border"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {(selectedStatuses.length > 0 ||
                selectedUser ||
                selectedDocumentIds.length > 0) && (
                <Badge variant="secondary" className="ml-2">
                  {[
                    selectedStatuses.length > 0 &&
                      `${selectedStatuses.length} status`,
                    selectedUser && "1 user",
                    selectedDocumentIds.length > 0 &&
                      `${selectedDocumentIds.length} doc`,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-5 space-y-6 bg-card border border-border rounded-lg shadow-lg">
            {/* Status Multi-Select */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Status
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={
                    selectedStatuses.length === 0 ? "default" : "outline"
                  }
                  onClick={() => setSelectedStatuses([])}
                  className={cn(
                    filterButtonClass,
                    selectedStatuses.length === 0
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                {statusOptions.map((status) => (
                  <Button
                    key={status.value}
                    size="sm"
                    variant={
                      selectedStatuses.includes(status.value)
                        ? "default"
                        : "outline"
                    }
                    onClick={() => {
                      setSelectedStatuses((prev) =>
                        prev.includes(status.value)
                          ? prev.filter((s) => s !== status.value)
                          : [...prev, status.value],
                      );
                    }}
                    className={cn(
                      filterButtonClass,
                      selectedStatuses.includes(status.value)
                        ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                        : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    {status.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* User Dropdown with Search */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                User
              </span>
              <Popover
                open={userDropdownOpen}
                onOpenChange={setUserDropdownOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={userDropdownOpen}
                    className="w-full justify-between"
                  >
                    {selectedUser
                      ? `${selectedUser.firstName} ${selectedUser.lastName}`
                      : "Select user..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[360px] p-2">
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Search users..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>

                    {/* Clear selection option */}
                    <div
                      className={cn(
                        "flex items-center justify-between px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent hover:text-accent-foreground",
                        !selectedUser && "bg-accent text-accent-foreground",
                      )}
                      onClick={() => {
                        setSelectedUser(null);
                        setUserDropdownOpen(false);
                      }}
                    >
                      <span className="text-sm font-medium">All Users</span>
                      {!selectedUser && <Check className="h-4 w-4" />}
                    </div>

                    <div className="max-h-[200px] overflow-y-auto border-t pt-2">
                      {users &&
                        users
                          .filter((user) =>
                            userSearchTerm.trim() === ""
                              ? true
                              : `${user.firstName} ${user.lastName} ${user.email}`
                                  .toLowerCase()
                                  .includes(userSearchTerm.toLowerCase()),
                          )
                          .sort((a, b) =>
                            `${a.firstName} ${a.lastName}`.localeCompare(
                              `${b.firstName} ${b.lastName}`,
                            ),
                          )
                          .map((user) => (
                            <div
                              key={user.id}
                              className={cn(
                                "flex items-center justify-between px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent hover:text-accent-foreground",
                                selectedUser?.id === user.id &&
                                  "bg-accent text-accent-foreground",
                              )}
                              onClick={() => {
                                setSelectedUser(user);
                                setUserDropdownOpen(false);
                                setUserSearchTerm("");
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="text-sm font-medium">
                                  {user.firstName} {user.lastName}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {user.email}
                                </span>
                              </div>
                              {selectedUser?.id === user.id && (
                                <Check className="h-4 w-4" />
                              )}
                            </div>
                          ))}
                      {users &&
                        userSearchTerm.trim() !== "" &&
                        users.filter((user) =>
                          `${user.firstName} ${user.lastName} ${user.email}`
                            .toLowerCase()
                            .includes(userSearchTerm.toLowerCase()),
                        ).length === 0 && (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            No users found.
                          </div>
                        )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Documents Multi-Select */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Documents
              </span>
              <div className="flex gap-2 flex-wrap max-h-32 overflow-y-auto">
                <Button
                  size="sm"
                  variant={
                    selectedDocumentIds.length === 0 ? "default" : "outline"
                  }
                  onClick={() => setSelectedDocumentIds([])}
                  className={cn(
                    filterButtonClass,
                    selectedDocumentIds.length === 0
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                {documents?.content &&
                  documents.content
                    .sort((a, b) => a.title.localeCompare(b.title))
                    .map((document) => (
                      <Button
                        key={document.id}
                        size="sm"
                        variant={
                          selectedDocumentIds.includes(document.id)
                            ? "default"
                            : "outline"
                        }
                        onClick={() => {
                          setSelectedDocumentIds((prev) =>
                            prev.includes(document.id)
                              ? prev.filter((id) => id !== document.id)
                              : [...prev, document.id],
                          );
                        }}
                        className={cn(
                          filterButtonClass,
                          selectedDocumentIds.includes(document.id)
                            ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                            : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                        )}
                      >
                        {document.title}
                      </Button>
                    ))}
              </div>
            </div>

            {/* Clear All Filters */}
            {(selectedStatuses.length > 0 ||
              selectedUser ||
              selectedDocumentIds.length > 0) && (
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedStatuses([]);
                    setSelectedUser(null);
                    setSelectedDocumentIds([]);
                    setUserSearchTerm("");
                  }}
                  className="w-full"
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Assignments Table */}
      <AssignmentTable
        assignments={assignmentsData?.content || []}
        totalPages={assignmentsData?.page?.totalPages || 0}
        currentPage={assignmentsData?.page?.number ?? (filters.page || 0)}
        isLoading={isLoading}
        onDelete={handleDelete}
        onPageChange={handlePageChange}
      />

      {/* Modals */}
      {showCreateModal && (
        <CreateAssignmentModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["assignments"] });
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};

export default AssignmentManagement;
